/*
 * Copyright (c) 2024-2026 Nodals AI Ltd
 * All rights reserved.
 */

import { AccidentalClickDetector } from '../../activity/accidental-click';
import {
  INVALID_CLICK_WINDOW_MS,
  NAV_EVIDENCE_WINDOW_MS,
  PROXIMITY_EVIDENCE_MIN_LATENCY_MS,
} from '../../activity/constants';
import { FrequencyStore, type FrequencyContext } from '../../activity/frequency';
import { ActivityManager } from '../../activity/manager';
import { Opportunity } from '../../auction';
import {
  Campaign,
  OBSERVATIONAL_CAMPAIGN_TYPES,
  UNTAGGED_MONITORED_CAMPAIGN_TYPES,
} from '../../campaigns/campaign';
import { EventDispatcher } from '../../events';
import { Logger } from '../../logger';
import { Facts } from '../../rules';
import { AdIdType, CampaignType } from '../../types';
import { clone, debugPixel, generateUUID, getGlobalPrebidObj } from '../../util';
import { createVendorFormatAdapters } from '../formats';
import { DIMENSIONS_UNKNOWN, NATIVE_SIZE_TOKEN, PlacementType, Visibility } from './constants';
import { PlacementEvents } from './events';

import type { FormatDetection, VendorFormat } from '../../types/formats';
import type {
  AdAssignedKeyValues,
  CampaignData,
  CampaignEligibleAds,
  KeyValue,
  PlacementAssignedKeyValues,
  PlacementSize,
  RenderedAdData,
} from '../../types/index';
import type {
  PrebidAdRenderFailedData,
  PrebidAdRenderSucceededData,
  PrebidAdUnit,
  PrebidAdUnitSize,
} from '../../types/prebid';
import type { SiteConfig } from '../config';
import type { VendorFormatAdapter } from '../formats';

const logger = Logger.getInstance();
const MAX_STORED_OPPORTUNITIES = 5;
// Shared empty list so the base hook allocates nothing per impression dispatch.
const NO_VENDOR_FORMATS: readonly VendorFormat[] = [];

/**
 * Minimal interface covering the placement properties required by the analytics pipeline.
 * Both Placement (auction-managed) and AdvertorialPlacement (DOM-discovered) implement this,
 * allowing the analytics recorder to handle both paths uniformly.
 */
export interface TrackablePlacement {
  adUnitCode: string;
  visibility: Visibility;
  supportedSizes: PlacementSize[];
  placementType: PlacementType;
}

abstract class Placement extends EventDispatcher<PlacementEvents> implements TrackablePlacement {
  private _adUnit: PrebidAdUnit;
  private campaignData: CampaignData[];
  private _campaigns: Campaign[] = [];
  private _facts: Facts = {};
  private _visibility: Visibility = Visibility.UNKNOWN;
  private _keyValues: KeyValue[] = [];
  private _supportedSizes: PlacementSize[] = [];
  private adUnitTransformer: (adUnitCode: string) => string;
  private impressionDeliveryId: string | null = null;
  // Opportunity id captured when the impression is dispatched. Interaction,
  // viewable and accidental-click events attribute to THIS id rather than
  // currentOpportunity: page-level auctions frequently mint new opportunities
  // for placements whose slot never re-rendered, and a late event must report
  // against the impression that was actually rendered/clicked.
  private impressionOpportunityId: string | null = null;

  protected opportunities: Opportunity[] = [];
  protected delegateImpressionTracking: boolean = false;
  protected renderedAdData: RenderedAdData | null = null;
  protected renderedSlotSize: PlacementSize = null;
  protected partialAdData: Partial<RenderedAdData> | null = null;
  protected currentDeliveryId: string | null = null;
  protected slot: any = null;
  // Whether this placement accepts native creatives (prebid native mediaType,
  // or - for GPT - a slot declaring 'fluid' or 1x1). Surfaces as the 'native'
  // token in the placement.sizes fact.
  protected supportsNative: boolean = false;
  protected interactionListenerAdded: boolean = false;
  protected adInteractionLoggedForRender: boolean = false;
  protected slotRenderedForOpportunity: boolean = false;
  protected enablePrebidRenderListenerForOpportunity: boolean = false;
  protected prebidAdRenderedForOpportunity: boolean = false;
  protected directImpressionDispatchedForOpportunity: boolean = false;
  protected slotViewedForRender: boolean = false;
  protected wrappingDomElement: HTMLElement | null = null;
  protected originalElementFilter: string = 'none';
  protected viewableImpressionMissed: boolean = false;
  protected siteConfig: SiteConfig;
  protected placementFrame: HTMLIFrameElement | null = null;

  // Vendor adapters for takeover formats, created by the factory from the
  // placement's declared vendor list plus page-environment probes, and cached
  // per vendor so per-render state and teardown stay placement-scoped. Built
  // lazily on first detection: placements that host no vendor formats (AST,
  // video) never allocate the map. Empty on vendor-free pages.
  private vendorFormatAdapterCache: Map<string, VendorFormatAdapter> | null = null;
  protected formatDetection: FormatDetection | null = null;

  private mouseEnteredIframe: boolean = false;
  private winBlurListener?: () => void;
  private iframeFocusListener?: () => void;
  private iframeClickListener?: () => void;
  private iframeMouseEnterListener?: () => void;
  private iframeMouseLeaveListener?: () => void;
  private nativeWindowOpen?: Window['open'];
  // Navigation-evidence gate: heuristic interaction signals open a candidate
  // that is only dispatched once the page emits navigation evidence
  // (visibilitychange -> hidden / pagehide) within NAV_EVIDENCE_WINDOW_MS.
  private pendingInteractionTrigger: string | null = null;
  // Proximity-class candidates (hover-corroborated) must show a minimum
  // candidate-to-evidence latency to confirm; see
  // PROXIMITY_EVIDENCE_MIN_LATENCY_MS for the rationale.
  private pendingInteractionMinEvidenceMs: number = 0;
  // Latch so AdHiddenRepeatSignal counts repeat CLICKS (max one per render),
  // not every tab-switch made with the pointer over an already-clicked ad.
  private repeatSignalLoggedForRender: boolean = false;
  private pendingInteractionAt: number = 0;
  private pendingInteractionTimerId: ReturnType<typeof setTimeout> | null = null;
  private pageVisibilityListener?: () => void;
  private pageHideListener?: () => void;
  // Detect the first return up to an hour after an interaction so the /iclk
  // pixel can study return-time distribution. The analytics layer decides which
  // pixels actually fire (/aclk only when the return is within BACK_CLICK_WINDOW_MS).
  private accidentalClickDetector: AccidentalClickDetector = new AccidentalClickDetector(
    INVALID_CLICK_WINDOW_MS
  );
  private adInteractionTimeoutId: NodeJS.Timeout | null = null;
  private slotDetectionIntervalId: NodeJS.Timeout | null = null;
  private slotDetectionAttempts: number = 0;
  private brandSafetyActivated: boolean = false;
  private brandSafetyViolations: Map<string, { detectedWords: string[] }> = new Map();
  private recordedBrandSafetyViolationsForOpportunity: Set<string> = new Set();
  private isBlurred: boolean = false;
  private readonly maxSlotDetectionAttempts: number = 6;
  private readonly slotDetectionIntervalMs: number = 500;

  constructor(
    adUnit: PrebidAdUnit,
    adUnitTransformer: (adUnitCode: string) => string,
    campaignData: CampaignData[],
    siteConfig: SiteConfig
  ) {
    super();
    this._adUnit = adUnit;
    this.adUnitTransformer = adUnitTransformer;
    this.campaignData = campaignData;
    this.siteConfig = siteConfig;
  }

  public init(): boolean {
    this.initialiseSupportedSizes();
    this.determineSlot();
    this.setFacts();
    this.initialiseCampaigns();
    if (!this.slot) {
      logger.warn(`Placement for adUnit ${this.adUnitCode} has no valid slot`);
      return false;
    }
    return true;
  }

  public determineSlot(): void {
    if (!this.slot) {
      this.setSlot();
      if (this.slot) {
        this.clearSlotDetectionInterval();
        this.setWrappingDomElement();
        this.determineVisibility();
        this.setSupportedSizes();
        this.addListeners();
      } else {
        this.startSlotDetectionInterval();
      }
    }
  }

  private startSlotDetectionInterval(): void {
    if (this.slotDetectionIntervalId !== null) {
      return;
    }

    this.slotDetectionAttempts = 0;
    this.slotDetectionIntervalId = setInterval(() => {
      this.slotDetectionAttempts++;

      if (this.slotDetectionAttempts >= this.maxSlotDetectionAttempts) {
        this.clearSlotDetectionInterval();
        logger.warn(
          `Failed to detect slot for adUnit ${this.adUnitCode} after ${this.maxSlotDetectionAttempts} attempts`
        );
        return;
      }

      this.determineSlot();
    }, this.slotDetectionIntervalMs);
  }

  private clearSlotDetectionInterval(): void {
    if (this.slotDetectionIntervalId !== null) {
      clearInterval(this.slotDetectionIntervalId);
      this.slotDetectionIntervalId = null;
    }
  }

  private clearAdInteractionTimeout(): void {
    if (this.adInteractionTimeoutId) {
      clearTimeout(this.adInteractionTimeoutId);
      this.adInteractionTimeoutId = null;
    }
  }

  public destroy(): void {
    this.clearSlotDetectionInterval();
    this.clearAdInteractionTimeout();
    this.clearPendingInteractionCandidate();
    this.accidentalClickDetector.disarm();
  }

  public hasValidSlot(): boolean {
    return this.slot !== null;
  }

  public get adUnit(): PrebidAdUnit {
    return this._adUnit;
  }

  public get adUnitCode(): string {
    return this.adUnitTransformer(this._adUnit.code);
  }

  public get currentOpportunity(): Opportunity | null {
    return this.opportunities.length > 0 ? this.opportunities[this.opportunities.length - 1] : null;
  }

  public get facts(): Facts {
    return clone(this._facts);
  }

  /**
   * Returns the native slot object of the tagging system (e.g., GPT slot object).
   */
  public get nativeSlotObject(): any {
    return this.slot;
  }

  public get domElement(): HTMLElement | null {
    return this.wrappingDomElement;
  }

  public get visibility(): Visibility {
    return this._visibility;
  }

  /**
   * Placement type sent to the backend (select endpoint `t`, impression pixel
   * `plcmt_t`). Defaults to DISPLAY, covering GPT/AST placements; video
   * placements override this.
   */
  public get placementType(): PlacementType {
    return PlacementType.DISPLAY;
  }

  public get supportedSizes(): PlacementSize[] {
    return this._supportedSizes;
  }

  public isLinkedToAdUnit(adUnitCode: string): boolean {
    if (this.adUnitTransformer(adUnitCode) === this.adUnitCode) {
      return true;
    }
    return false;
  }

  public newOpportunity(opportunity: Opportunity): void {
    // TODO: Check slot validity again.
    logger.info(`New opportunity ${opportunity.id} for placement ${this.adUnitCode}`);
    this.opportunities.push(opportunity);
    if (this.opportunities.length > MAX_STORED_OPPORTUNITIES) {
      this.opportunities.shift();
    }
    this.determineVisibility();
    this.resetStateForNewOpportunity();
    this.clearKeyValues();
    this.setFacts();
    // Only auction-scoped state is reset here. State describing a RENDERED ad
    // (impression context, interaction listeners, one-shot latches) is reset in
    // resetStateForNewRender() when the slot itself re-renders: page-level
    // auctions frequently include ad units whose slots never re-render (e.g. a
    // publisher running full-list refresh auctions), and an ad that is still
    // displayed must remain clickable-and-countable until its slot genuinely
    // re-renders.
    this.resetOpportunityScopedState();
    if (!this.brandSafetyActivated) {
      this.activateBrandSafetyMonitor();
    } else {
      this.evaluateBrandSafetyStatus();
    }
  }

  // Reset the one-shot latches that gate per-opportunity processing. Shared by
  // newOpportunity() (auction-minted) and mintRenderOpportunity()
  // (render-minted) so the two paths cannot drift.
  private resetOpportunityScopedState(): void {
    this.slotRenderedForOpportunity = false;
    this.enablePrebidRenderListenerForOpportunity = false;
    this.prebidAdRenderedForOpportunity = false;
    this.directImpressionDispatchedForOpportunity = false;
    this.recordedBrandSafetyViolationsForOpportunity.clear();
    this.partialAdData = null;
  }

  /**
   * Mint a placement-local opportunity for a render that arrived without a
   * fresh auction (a publisher-driven ad-server refresh with no Prebid
   * auction). Opportunity ids are join keys only, so a locally generated id is
   * valid — video placements already mint one per follow-on ad the same way.
   *
   * Deliberately narrower than newOpportunity(): key values are NOT cleared
   * (the ad-server targeting that caused this render persists across
   * refreshes, and clearing campaign key-values would misclassify the render's
   * impression), facts are not re-evaluated (no bid request follows), and the
   * auction-scoped subclass hook is not invoked.
   */
  protected mintRenderOpportunity(): void {
    const opportunity = new Opportunity(generateUUID());
    logger.info(`Minted render opportunity ${opportunity.id} for placement ${this.adUnitCode}`);
    this.opportunities.push(opportunity);
    if (this.opportunities.length > MAX_STORED_OPPORTUNITIES) {
      this.opportunities.shift();
    }
    // A render-minted opportunity has no auction moment to freeze page
    // signals against, so render time is the reference point — without this
    // snapshot every auction-less refresh impression records with no xd.
    FrequencyStore.getInstance().captureRenderPageSignals(opportunity.id);
    this.determineVisibility();
    this.resetOpportunityScopedState();
  }

  /**
   * Reset state bound to a rendered ad. Tagging subclasses invoke this the
   * moment a NEW render is detected for this slot (GPT slotRenderEnded, AST
   * adSlotRenderStart / native impression, video ad start) — the previously
   * rendered ad no longer exists, so the listeners, impression context and
   * one-shot latches that described it must not survive onto the new render.
   *
   * Deliberately NOT invoked from newOpportunity(): an opportunity is an
   * auction-participation event, not a render event, and tearing down here on
   * every auction made clicks on long-lived renders (e.g. Gumtree's gallery
   * MPU, rendered once into a photo carousel and clickable for the whole page
   * view) structurally uncountable after the first refresh auction.
   *
   * A render that arrives WITHOUT a fresh opportunity (auction-less GPT
   * refresh) also passes through here — tearing down the stale state — and the
   * tagging subclass then mints a placement-local opportunity via
   * mintRenderOpportunity() so the new render's impression and interactions
   * are counted. One-impression-per-opportunity semantics are preserved: every
   * tracked render owns exactly one opportunity, auction-minted or
   * render-minted.
   */
  protected resetStateForNewRender(): void {
    this.clearAdInteractionTimeout();
    // Remove interaction listeners armed for the previous render — in
    // particular the window-level blur listener, which is not iframe-scoped
    // and would otherwise fire against the new render and dispatch an
    // interaction attributed to the previous impression.
    this.removeAdInteractionListeners();
    this.interactionListenerAdded = false;
    this.adInteractionLoggedForRender = false;
    this.slotViewedForRender = false;
    this.renderedAdData = null;
    this.renderedSlotSize = null;
    // Clear the impression context so events for the incoming render (e.g. a
    // video pod's later ad) cannot be emitted against the previous ad's
    // delivery id before its own impression is recorded.
    this.impressionDeliveryId = null;
    this.impressionOpportunityId = null;
    this.repeatSignalLoggedForRender = false;
    // Lift the detector's one-shot latch so the new render gets its own
    // accidental-click detection window (the instance is shared across the
    // placement's lifetime).
    this.accidentalClickDetector.reset();
    this.viewableImpressionMissed = false;
    this.mouseEnteredIframe = false;
    this.placementFrame = null;
    // Vendor format detection and its captured surfaces are render-scoped.
    this.vendorFormatAdapterCache?.forEach((adapter) => adapter.dispose());
    this.formatDetection = null;
  }

  private initialiseCampaigns(): void {
    this._campaigns = this.campaignData.map((data) => new Campaign(data, this.facts));
    logger.debug(
      `Placement initialised with ${this._campaigns.length} campaigns for adUnit ${this.adUnitCode}`
    );
  }

  public updateCampaigns(campaignData: CampaignData[]): void {
    this.campaignData = campaignData;
    this._campaigns = this.campaignData.map((data) => new Campaign(data, this.facts));
    this.brandSafetyActivated = false; // New campaigns, need to reactivate
    this.activateBrandSafetyMonitor();
    logger.debug(
      `Placement campaigns updated with ${this._campaigns.length} campaigns for adUnit ${this.adUnitCode}`
    );
  }

  public setKeyValues(placementKeyValues: PlacementAssignedKeyValues): void {
    if (placementKeyValues.kvs) {
      this._keyValues = [...placementKeyValues.kvs];
    }
    // Set key values on campaigns if ad-specific key values are provided
    if (placementKeyValues.ads) {
      this._campaigns.forEach((campaign) => {
        campaign.adKeyValues = placementKeyValues.ads!;
      });
    }
  }

  private clearKeyValues(): void {
    this._campaigns.forEach((campaign) => {
      campaign.clearAdKeyValues();
    });
  }

  public hasAnyCampaignForTypes(campaignTypes: CampaignType[]): boolean {
    return this._campaigns.some((campaign) => campaignTypes.includes(campaign.type));
  }

  public getKeyValuesForCampaignType(campaignTypes: CampaignType[]): KeyValue[] {
    const allKeyValues: KeyValue[] = [];
    allKeyValues.push(...clone(this._keyValues));

    this._campaigns
      .filter((campaign) => campaignTypes.includes(campaign.type))
      .forEach((campaign) => {
        allKeyValues.push(...clone(campaign.keyValues));
      });

    const mergedKeyValues: KeyValue[] = [];

    allKeyValues.forEach((kv) => {
      const existingKv = mergedKeyValues.find((existingKv) => existingKv.key === kv.key);
      if (!existingKv) {
        mergedKeyValues.push({ ...kv });
      } else {
        const existingValues = existingKv.value.split(',');
        if (!existingValues.includes(kv.value)) {
          existingKv.value += `,${kv.value}`;
          /*
          // Only add value if both existing and new value are not empty
          if (existingKv.value.length > 0 && kv.value.length > 0) {
            existingKv.value += `,${kv.value}`;
          } else if (kv.value.length > 0) {
            // Existing is empty, new value is not empty
            existingKv.value = kv.value;
          }
          // If kv.value is empty, we don't add anything (keep existing value)
          */
        }
      }
    });

    return mergedKeyValues;
  }

  /**
   * Lightweight eligibility probe: does any campaign of the given types have
   * an eligible ad on a brand-compliant page? Unlike
   * getEligibleAdIdsByCampaign this builds no payload objects and computes no
   * frequency snapshots — safe for gates that run over ALL campaign types
   * (e.g. the page-view gate), where snapshot registration would leak xd onto
   * observational (t=1) impressions.
   */
  public hasEligibleCampaigns(campaignTypes: CampaignType[]): boolean {
    return this._campaigns.some(
      (campaign) =>
        campaignTypes.includes(campaign.type) &&
        campaign.pageIsBrandCompliant &&
        campaign.hasEligibleAds()
    );
  }

  public getEligibleAdIdsByCampaign(
    campaignTypes: CampaignType[],
    freqCtx: FrequencyContext
  ): CampaignEligibleAds[] {
    const campaigns: CampaignEligibleAds[] = [];
    const frequencyStore = FrequencyStore.getInstance();
    // Snapshots are keyed by the auction's opportunity id so the exact values
    // sent to the select endpoint can be replayed onto the resulting
    // impression event ("what the model sees is what we record").
    const opportunityId = this.currentOpportunity?.id;

    this._campaigns
      .filter((campaign) => campaignTypes.includes(campaign.type))
      .forEach((campaign) => {
        const campaignEligibleAdIds: string[] = campaign.getEligibleAdIds();
        if (campaignEligibleAdIds.length > 0 && campaign.pageIsBrandCompliant) {
          const campaignFrequency = frequencyStore.getCampaignFrequency(
            opportunityId,
            campaign.id,
            campaign.adDeliveryIds,
            freqCtx
          );
          campaigns.push({
            id: campaign.id,
            edids: campaignEligibleAdIds,
            eads: campaignEligibleAdIds.map((deliveryId) => ({ id: deliveryId })),
            cel: campaignFrequency.cel,
            ceh: campaignFrequency.ceh,
            ced: campaignFrequency.ced,
            cew: campaignFrequency.cew,
          });
        }
      });

    return campaigns;
  }

  public getAdKeyValuesByAd(): AdAssignedKeyValues[] {
    const adKeyValues: AdAssignedKeyValues[] = [];

    this._campaigns.forEach((campaign) => {
      adKeyValues.push(...campaign.keyValuesByAd);
    });
    return adKeyValues;
  }

  public getAdKeyValuesByAdForCampaignTypes(campaignTypes: CampaignType[]): AdAssignedKeyValues[] {
    const adKeyValues: AdAssignedKeyValues[] = [];

    this._campaigns
      .filter((campaign) => campaignTypes.includes(campaign.type))
      .forEach((campaign) => {
        adKeyValues.push(...campaign.keyValuesByAd);
      });

    return adKeyValues;
  }

  public hasDealActivated(dealId: string): boolean {
    return this._campaigns.some((campaign) => campaign.deals.includes(dealId));
  }

  public getLearningCampaignDeliveryId(campaignType: CampaignType): string | undefined {
    const learningCampaign = this._campaigns.find((campaign) => campaign.type === campaignType);
    return learningCampaign?.adDeliveryIds[0];
  }

  public getDealCampaignDeliveryId(dealId: string): string | undefined {
    const dealCampaign = this._campaigns.find((campaign) => campaign.deals.includes(dealId));
    return dealCampaign?.adDeliveryIds[0];
  }

  /**
   * Find matching ad delivery ID by line ID and/or creative ID.
   *
   * Two-pass match: first prefers ads whose key-values were set on the ad
   * call for the current opportunity (i.e. whose campaign rule actually
   * fired), then falls back to any matching ad. This disambiguates sibling
   * campaigns that share a creative ID — the ad whose rule fired wins. The
   * fallback covers monitoring ads (which never have key-values set) and
   * preserves the existing learning-fallback path in onTaggedAdServed for
   * scoring/direct ads whose rule did not fire.
   *
   * @param lineId Optional line ID to match
   * @param creativeId Optional creative ID to match
   * @returns The delivery ID of the matching ad, or undefined if no match
   */
  public getMatchingAdDeliveryId(lineId?: string, creativeId?: string): string | undefined {
    for (const campaign of this._campaigns) {
      const deliveryId = campaign.getMatchingAdDeliveryId(lineId, creativeId, true);
      if (deliveryId) {
        return deliveryId;
      }
    }
    for (const campaign of this._campaigns) {
      const deliveryId = campaign.getMatchingAdDeliveryId(lineId, creativeId);
      if (deliveryId) {
        return deliveryId;
      }
    }
    return undefined;
  }

  /**
   * Find campaign associated with the given external ID(s).
   *
   * Two-pass match mirroring getMatchingAdDeliveryId: prefer a campaign with
   * an ad whose key-values were set on the ad call, then fall back to any
   * matching campaign. Keeping the two in sync ensures impression attribution
   * and the learning-fallback decision in onTaggedAdServed use the same
   * campaign.
   *
   * @param lineId Optional line ID to match
   * @param creativeId Optional creative ID to match
   * @returns The first matching campaign, or undefined if no match
   */
  public getAssociatedCampaign(lineId?: string, creativeId?: string): Campaign | undefined {
    for (const campaign of this._campaigns) {
      if (campaign.isAssociatedWith(lineId, creativeId, true)) {
        return campaign;
      }
    }
    for (const campaign of this._campaigns) {
      if (campaign.isAssociatedWith(lineId, creativeId)) {
        return campaign;
      }
    }
    return undefined;
  }

  /**
   * @deprecated Use getAssociatedCampaign() instead
   */
  public getLineAssociatedCampaign(lineId: string): Campaign | undefined {
    return this.getAssociatedCampaign(lineId);
  }

  public getSelectionLabel(id: string, type: AdIdType): string | undefined {
    for (const campaign of this._campaigns) {
      const label = campaign.getAdSelectionLabel(id, type);
      if (label !== undefined) {
        return label;
      }
    }
    return undefined;
  }

  public getUntaggedMonitoredLineIds(): string[] {
    const untaggedLineIds: string[] = [];

    this._campaigns.forEach((campaign) => {
      if (UNTAGGED_MONITORED_CAMPAIGN_TYPES.includes(campaign.type)) {
        untaggedLineIds.push(...campaign.linkedExternalLineIds);
      }
    });

    return untaggedLineIds;
  }

  public getUntaggedMonitoredCreativeIds(): string[] {
    const untaggedCreativeIds: string[] = [];

    this._campaigns.forEach((campaign) => {
      if (UNTAGGED_MONITORED_CAMPAIGN_TYPES.includes(campaign.type)) {
        untaggedCreativeIds.push(...campaign.linkedExternalCreativeIds);
      }
    });

    return untaggedCreativeIds;
  }

  private isNonLearningCampaignDeliveryId(deliveryId: string): boolean {
    for (const campaign of this._campaigns) {
      if (campaign.adDeliveryIds.includes(deliveryId)) {
        // Monitoring campaigns are passive observers and are treated as
        // learning-equivalent for event-emission purposes (t=1).
        return !OBSERVATIONAL_CAMPAIGN_TYPES.includes(campaign.type);
      }
    }
    return false;
  }

  protected isLearningCampaignDeliveryId(deliveryId: string): boolean {
    return !this.isNonLearningCampaignDeliveryId(deliveryId);
  }

  public handleTaggedAdDelivery(deliveryId: string, impressionId: string): void {
    if (!this.partialAdData) {
      logger.warn(
        `No partial ad data found for placement ${this.adUnitCode} when handling TaggedAdServed`
      );
      return;
    }

    this.renderedAdData = {
      ...this.partialAdData,
      responseIdentifier: impressionId,
    } as RenderedAdData;
    this.partialAdData = null;

    let keyValues: KeyValue[] = [];
    for (const campaign of this._campaigns) {
      const campaignKeyValues = campaign.getAdKeyValues(deliveryId, AdIdType.DELIVERY_ID);
      if (campaignKeyValues.length > 0) {
        keyValues = campaignKeyValues;
        break;
      }
    }
    this.dispatchDirectServedAdImpression(deliveryId, keyValues || undefined);

    // The Nodals tag only ever posts ad.serve; it does not report interactions.
    // Attach the library-side interaction listeners now that the impression is
    // recorded so tagged ads get interaction tracking and, via
    // dispatchAdInteraction, arm the accidental-click detector.
    this.addAdInteractionListener();

    // Clear the parked viewable only when the replay actually dispatched: if
    // the dispatch above declined (e.g. no impression context was established
    // for this render), the flag must survive for a later replay attempt.
    if (this.viewableImpressionMissed && this.dispatchViewableImpression()) {
      this.viewableImpressionMissed = false;
    }
  }

  protected getSiteConfig(): SiteConfig {
    return this.siteConfig;
  }

  protected getImpressionDeliveryId(): string | null {
    return this.impressionDeliveryId;
  }

  protected resetStateForNewOpportunity(): void {}

  protected isVisible(visible: boolean = false): void {
    this._visibility = visible ? Visibility.VISIBLE : Visibility.NOT_VISIBLE;
  }

  protected checkElementVisibility(element: HTMLElement, size: PlacementSize): boolean {
    if (!element || !size) {
      return false;
    }

    const rect = element.getBoundingClientRect();
    const elementArea = size.width * size.height;

    if (elementArea === 0) {
      return false;
    }

    const adLeft = rect.left + (rect.width - size.width) / 2;
    const adTop = rect.top + (rect.height - size.height) / 2;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

    const visibleWidth = Math.max(
      0,
      Math.min(adLeft + size.width, viewportWidth) - Math.max(adLeft, 0)
    );

    const visibleHeight = Math.max(
      0,
      Math.min(adTop + size.height, viewportHeight) - Math.max(adTop, 0)
    );

    const visibleArea = visibleWidth * visibleHeight;
    if (visibleArea >= elementArea / 2) {
      return true;
    }
    return false;
  }

  protected addSupportedSize(size: PlacementSize): void {
    if (!size) {
      return;
    }
    if (
      !this.supportedSizes.some(
        // @ts-ignore
        (existingSize) => existingSize.width === size.width && existingSize.height === size.height
      )
    ) {
      this.supportedSizes.push(size);
    }
  }

  protected getSmallestSupportedSize(): PlacementSize {
    if (this.supportedSizes.length === 0) {
      return null;
    }
    let smallest = this.supportedSizes[0];
    this.supportedSizes.forEach((size) => {
      if (size!.width * size!.height < smallest!.width * smallest!.height) {
        smallest = size;
      }
    });
    return smallest;
  }

  protected cleanAdvertiserDomains(domains: string[]): string[] {
    return domains
      .map((domain) => domain.replace(/^https?:\/\//, ''))
      .filter((domain) => domain.includes('.'));
  }

  protected addPrebidAdRenderedListener(): void {
    const pbjs = getGlobalPrebidObj();
    if (pbjs) {
      const prebidAdRenderedEventHandler = (data: PrebidAdRenderSucceededData) => {
        if (!this.enablePrebidRenderListenerForOpportunity) {
          return;
        }
        if (this.isLinkedToAdUnit(data.bid.adUnitCode) && !this.prebidAdRenderedForOpportunity) {
          const cleanedDomains = this.cleanAdvertiserDomains(
            data.bid.meta?.advertiserDomains ?? []
          );

          if (cleanedDomains.length === 0) {
            logger.debug(
              `Ignoring Prebid AdRenderSucceeded for ${this.adUnitCode}: no valid advertiser domains`
            );
            return;
          }

          let deltaSeconds = Math.min(Math.round(this.currentOpportunity!.age / 1000), 20);
          debugPixel({
            en: 'prebidAdRendered',
            adUnitCode: this.adUnitCode,
            renderTimeDelta: String(deltaSeconds),
          });
          logger.debug(
            `Prebid AdRenderSucceeded event received for ${this.adUnitCode}, data: ${JSON.stringify(data.bid)}`
          );

          this.prebidAdRenderedForOpportunity = true;
          this.renderedAdData = {
            advertiserDomains: cleanedDomains,
            // transactionId is per-ad-unit-per-auction, so it collides when a
            // publisher serves multiple bids from one auction across successive
            // refreshes of the same slot. Concatenate the bid's adId (unique per
            // bid response) to keep the impression id unique per render while
            // retaining the transactionId prefix as a bid-reuse diagnostic signal.
            responseIdentifier: data.bid.transactionId
              ? `${data.bid.transactionId}:${data.bid.adId ?? generateUUID()}`
              : generateUUID(),
            isProgrammaticBuy: true,
            isPrebidAd: true,
            nodalsDealId:
              data.bid.dealId && this.hasDealActivated(data.bid.dealId)
                ? data.bid.dealId
                : undefined,
            size: this.renderedSlotSize ?? undefined,
          };
          const deliveryId = this.renderedAdData.nodalsDealId
            ? this.getDealCampaignDeliveryId(this.renderedAdData.nodalsDealId)
            : this.getLearningCampaignDeliveryId(CampaignType.LEARNING_PROGRAMMATIC);
          if (deliveryId) {
            const bidSize =
              data.bid.width && data.bid.height
                ? { width: data.bid.width, height: data.bid.height }
                : null;
            this.dispatchProgrammaticServedAdImpression(deliveryId, data.bid.ad, bidSize);
          }
        }
      };
      pbjs.onEvent('adRenderSucceeded', prebidAdRenderedEventHandler);

      const prebidAdRenderedFailedEventHandler = (data: PrebidAdRenderFailedData) => {
        if (this.isLinkedToAdUnit(data.bid.adUnitCode) && !this.prebidAdRenderedForOpportunity) {
          debugPixel({
            en: 'prebidAdRenderFailed',
            adUnitCode: this.adUnitCode,
          });
        }
      };
      pbjs.onEvent('adRenderFailed', prebidAdRenderedFailedEventHandler);
    } else {
      logger.info(
        `Unable to add Prebid listeners for ${this.adUnitCode}: Global Prebid object not found`
      );
    }
  }

  protected dispatchDirectServedAdImpression(
    deliveryId: string,
    keyValues: KeyValue[] | undefined = undefined
  ): void {
    // One direct-served impression per opportunity. A GptPlacement maps to a
    // single slot and a fresh opportunity is minted per auction — or per
    // tracked auction-less refresh render — so a second dispatch for the same
    // opportunity is always a duplicate render of the same ad. This guards
    // against an upstream double-fire (e.g. a slot re-rendering the same line
    // item within one opportunity) recording the impression twice.
    if (this.directImpressionDispatchedForOpportunity) {
      return;
    }
    const adData: RenderedAdData = this.renderedAdData as RenderedAdData;
    const currentOpportunity = this.currentOpportunity;
    if (adData && currentOpportunity) {
      this.detectVendorFormatForRender();
      this.directImpressionDispatchedForOpportunity = true;
      this.impressionDeliveryId = deliveryId;
      this.impressionOpportunityId = currentOpportunity.id;

      // Read the opportunity snapshot BEFORE recording this impression. Page
      // signals belong to every tracked render (learning included) — frozen
      // at auction time for auction-minted opportunities, at render time for
      // render-minted ones; campaign exposure is additionally present when
      // the delivery was selected. The snapshot excludes the impression being
      // dispatched. Absent only when the snapshot no longer exists (evicted
      // from the store before dispatch) → no xd.
      const impressionXd = FrequencyStore.getInstance().getImpressionXd(
        currentOpportunity.id,
        deliveryId
      );

      if (this.isNonLearningCampaignDeliveryId(deliveryId)) {
        ActivityManager.getInstance().recordImp(deliveryId, adData.responseIdentifier);
      }

      this.dispatch('DirectServedAdImpression', {
        id: adData.responseIdentifier,
        opportunityId: currentOpportunity.id,
        deliveryId: deliveryId,
        advertiserId: adData.advertiserId as string,
        lineItemId: adData.lineItemId as string,
        creativeId: adData.creativeId,
        advertiserDomains: adData.advertiserDomains as string[] | undefined,
        size: adData.size as PlacementSize,
        kvs: keyValues,
        durationSeconds: adData?.durationSeconds,
        selectionLabel: this.getSelectionLabel(deliveryId, AdIdType.DELIVERY_ID),
        t: this.isLearningCampaignDeliveryId(deliveryId) ? 1 : 0,
        v: this.visibility,
        ...(impressionXd && { xd: impressionXd }),
      });
    }
  }

  protected dispatchProgrammaticServedAdImpression(
    deliveryId: string,
    bidAdMarkup?: string,
    bidSize?: PlacementSize
  ): void {
    const adData: RenderedAdData = this.renderedAdData as RenderedAdData;
    const currentOpportunity = this.currentOpportunity;
    if (adData && currentOpportunity) {
      this.detectVendorFormatForRender(bidAdMarkup);
      this.impressionDeliveryId = deliveryId;
      this.impressionOpportunityId = currentOpportunity.id;

      // Auction-time snapshot: page signals for every tracked auction (this
      // path covers learning-programmatic and deal deliveries), plus campaign
      // exposure when the delivery was selected.
      const impressionXd = FrequencyStore.getInstance().getImpressionXd(
        currentOpportunity.id,
        deliveryId
      );

      // Record impression for non-learning campaigns (i.e., deal campaigns)
      if (this.isNonLearningCampaignDeliveryId(deliveryId)) {
        ActivityManager.getInstance().recordImp(deliveryId, adData.responseIdentifier);
      }

      // Skin deals are trafficked through 1x1/PUC creatives, so the GPT event
      // size misreports the render; the winning bid's size is the truthful one.
      let size = adData.size as PlacementSize;
      const gptSizeUnusable = !size || (size.width <= 1 && size.height <= 1);
      if (this.formatDetection && gptSizeUnusable && bidSize?.width && bidSize?.height) {
        size = bidSize;
      }

      this.dispatch('ProgrammaticServedAdImpression', {
        id: adData.responseIdentifier,
        opportunityId: currentOpportunity.id,
        deliveryId: deliveryId,
        advertiserDomains: adData.advertiserDomains as string[],
        size,
        durationSeconds: adData.durationSeconds,
        nodalsDealId: adData.nodalsDealId,
        selectionLabel: this.getSelectionLabel(deliveryId, AdIdType.DELIVERY_ID),
        t: 1,
        v: this.visibility,
        ...(impressionXd && { xd: impressionXd }),
      });
      this.addAdInteractionListener();
    }
  }

  protected dispatchViewableImpression(useHttpPost?: boolean): boolean {
    if (this.slotViewedForRender) {
      return false;
    }

    // If we don't have rendered ad data yet, check if we're waiting for a Nodals ad
    if (!this.renderedAdData) {
      if (this.partialAdData) {
        // We're waiting for TaggedAdServed - mark that we missed the viewable impression timing
        this.viewableImpressionMissed = true;
        logger.debug(
          `Viewable impression timing missed for placement ${this.adUnitCode}, will dispatch after TaggedAdServed`
        );
      }
      return false;
    }
    if (this.impressionOpportunityId && this.impressionDeliveryId) {
      if (this.isNonLearningCampaignDeliveryId(this.impressionDeliveryId)) {
        ActivityManager.getInstance().recordView(this.impressionDeliveryId);
      }

      this.dispatch('ViewableImpression', {
        impressionId: this.renderedAdData?.responseIdentifier ?? generateUUID(),
        opportunityId: this.impressionOpportunityId,
        deliveryId: this.impressionDeliveryId as string,
        advertiserId: this.renderedAdData?.advertiserId ?? undefined,
        advertiserDomains: this.renderedAdData?.advertiserDomains ?? undefined,
        lineItemId: this.renderedAdData?.lineItemId ?? undefined,
        nodalsDealId: this.renderedAdData?.nodalsDealId ?? undefined,
        useHttpPost,
        selectionLabel: this.getSelectionLabel(this.impressionDeliveryId!, AdIdType.DELIVERY_ID),
        t: this.isLearningCampaignDeliveryId(this.impressionDeliveryId!) ? 1 : 0,
      });
      this.slotViewedForRender = true;
      return true;
    }
    return false;
  }

  /**
   * Open an interaction candidate for a heuristic trigger (creative click,
   * creative focus, window-blur heuristics). The candidate is dispatched as an
   * AdInteraction only if navigation evidence (visibilitychange -> hidden or
   * pagehide, see onPossibleNavigation) arrives within NAV_EVIDENCE_WINDOW_MS.
   * A genuine clickthrough always removes the page from view; clicks on
   * creative UI (close buttons, carousel/video controls) never do — those
   * candidates expire and are reported via the AdInteractionCandidateNoNav
   * debug pixel instead of being counted.
   */
  protected registerInteractionCandidate(trigger: string): boolean {
    if (this.adInteractionLoggedForRender) {
      return false;
    }
    if (!this.impressionOpportunityId || !this.impressionDeliveryId) {
      return false;
    }
    this.ensureNavigationEvidenceListeners();
    this.pendingInteractionTrigger = trigger;
    // Hover-corroborated triggers watch page-spanning surfaces (skin rails),
    // where a parked cursor + tab-switch mimics a click; matched by name so
    // future vendor adapters inherit the guard.
    this.pendingInteractionMinEvidenceMs = /hover/i.test(trigger)
      ? PROXIMITY_EVIDENCE_MIN_LATENCY_MS
      : 0;
    this.pendingInteractionAt = Date.now();
    if (this.pendingInteractionTimerId) {
      clearTimeout(this.pendingInteractionTimerId);
    }
    this.pendingInteractionTimerId = setTimeout(() => {
      this.pendingInteractionTimerId = null;
      if (this.pendingInteractionTrigger !== null) {
        // forceLog: these gate pixels are the production tuning instrument
        // (candidate rejection volume, confirmation latency); click-scale
        // volume, so the cost is negligible.
        debugPixel(
          {
            en: 'AdInteractionCandidateNoNav',
            trigger: this.pendingInteractionTrigger,
            gad: this.adUnitCode,
            lid: (this.renderedAdData?.lineItemId as string) ?? '',
          },
          true
        );
        this.pendingInteractionTrigger = null;
      }
    }, NAV_EVIDENCE_WINDOW_MS);
    return true;
  }

  /**
   * Navigation evidence handler (visibilitychange -> hidden / pagehide). A
   * pending candidate is confirmed and dispatched immediately, skipping the
   * element-hidden check: containers like Gumtree's gallery carousel destroy
   * the ad slide at exactly the moment a real click navigates, and the click
   * belongs to the impression regardless.
   */
  private onPossibleNavigation(evidence: string): void {
    if (this.pendingInteractionTrigger === null) {
      // Repeat-click telemetry: the page went hidden while the pointer was
      // over an already-counted render. Not dispatched (one interaction per
      // render is deliberate) — this pixel quantifies how many ad-server
      // clicks that policy leaves uncounted.
      if (
        this.adInteractionLoggedForRender &&
        this.mouseEnteredIframe &&
        !this.repeatSignalLoggedForRender
      ) {
        this.repeatSignalLoggedForRender = true;
        debugPixel(
          {
            en: 'AdHiddenRepeatSignal',
            evidence,
            gad: this.adUnitCode,
            impid: this.renderedAdData?.responseIdentifier ?? '',
            lid: (this.renderedAdData?.lineItemId as string) ?? '',
          },
          true
        );
      }
      return;
    }
    const trigger = this.pendingInteractionTrigger;
    const latencyMs = Date.now() - this.pendingInteractionAt;
    if (latencyMs < this.pendingInteractionMinEvidenceMs) {
      // Candidate and evidence arrived as two symptoms of the same user
      // action (e.g. tab-switch with the cursor over a skin rail) - not a
      // click. Drop the candidate rather than let it self-confirm.
      debugPixel(
        {
          en: 'AdInteractionCandidateEvidenceTooFast',
          trigger,
          evidence,
          latencyMs: String(latencyMs),
          gad: this.adUnitCode,
          lid: (this.renderedAdData?.lineItemId as string) ?? '',
        },
        true
      );
      this.clearPendingInteractionCandidate();
      return;
    }
    this.clearPendingInteractionCandidate();
    if (
      this.dispatchAdInteraction(false, {
        immediate: true,
        skipHiddenCheck: true,
      })
    ) {
      debugPixel(
        {
          en: 'AdInteractionConfirmedNav',
          trigger,
          evidence,
          latencyMs: String(latencyMs),
          gad: this.adUnitCode,
          lid: (this.renderedAdData?.lineItemId as string) ?? '',
        },
        true
      );
    }
  }

  /** Silently drop any open candidate (confirm path, teardown, destroy). */
  private clearPendingInteractionCandidate(): void {
    if (this.pendingInteractionTimerId) {
      clearTimeout(this.pendingInteractionTimerId);
      this.pendingInteractionTimerId = null;
    }
    this.pendingInteractionTrigger = null;
    this.pendingInteractionMinEvidenceMs = 0;
  }

  public dispatchAdInteraction(
    useHttpPost?: boolean,
    opts?: { immediate?: boolean; skipHiddenCheck?: boolean }
  ): boolean {
    if (this.adInteractionLoggedForRender) {
      return false;
    }

    // Attribute to the opportunity captured at impression time, NOT
    // currentOpportunity: later page-level auctions may have minted new
    // opportunities without this slot re-rendering, and the click belongs to
    // the impression the user actually saw.
    const impressionOpportunityId = this.impressionOpportunityId;

    if (impressionOpportunityId && this.impressionDeliveryId) {
      this.adInteractionLoggedForRender = true;

      const fn = () => {
        if (opts?.skipHiddenCheck || !this.isElementHidden(this.wrappingDomElement)) {
          logger.info(`Dispatching AdInteraction for ${this.adUnitCode}`);

          if (
            this.impressionDeliveryId &&
            this.isNonLearningCampaignDeliveryId(this.impressionDeliveryId)
          ) {
            ActivityManager.getInstance().recordInteraction(this.impressionDeliveryId);
          }

          const impressionId = this.renderedAdData?.responseIdentifier ?? generateUUID();
          this.dispatch('AdInteraction', {
            impressionId,
            opportunityId: impressionOpportunityId,
            deliveryId: this.impressionDeliveryId as string,
            advertiserId: this.renderedAdData?.advertiserId ?? undefined,
            advertiserDomains: this.renderedAdData?.advertiserDomains ?? undefined,
            lineItemId: this.renderedAdData?.lineItemId ?? undefined,
            nodalsDealId: this.renderedAdData?.nodalsDealId ?? undefined,
            useHttpPost,
            selectionLabel: this.getSelectionLabel(
              this.impressionDeliveryId!,
              AdIdType.DELIVERY_ID
            ),
            t: this.isLearningCampaignDeliveryId(this.impressionDeliveryId!) ? 1 : 0,
          });

          // A display ad opens its destination in a new tab; if the user bounces
          // straight back, count the click as accidental.
          this.armAccidentalClickDetection(impressionId, impressionOpportunityId);
        } else {
          // Element was hidden at dispatch time, so we did not record an
          // interaction; release the latch so a later genuine trigger can.
          this.adInteractionLoggedForRender = false;
        }
        this.adInteractionTimeoutId = null;
      };

      if (useHttpPost || opts?.immediate) {
        fn();
      } else {
        this.clearAdInteractionTimeout();
        this.adInteractionTimeoutId = setTimeout(() => {
          fn();
        }, 1000);
      }
      return true;
    }
    return false;
  }

  /**
   * Arm accidental-click detection after an interaction has been counted. If the
   * user leaves the page (the ad's new tab takes focus) and returns within the
   * accidental window, dispatch an AccidentalClick carrying the away duration.
   */
  private armAccidentalClickDetection(impressionId: string, opportunityId: string): void {
    const deliveryId = this.impressionDeliveryId;
    if (!deliveryId) {
      return;
    }
    this.accidentalClickDetector.arm((awayDurationMs) => {
      logger.info(`Dispatching AccidentalClick for ${this.adUnitCode}`);
      this.dispatch('AccidentalClick', {
        impressionId,
        opportunityId,
        deliveryId,
        advertiserId: this.renderedAdData?.advertiserId ?? undefined,
        advertiserDomains: this.renderedAdData?.advertiserDomains ?? undefined,
        lineItemId: this.renderedAdData?.lineItemId ?? undefined,
        nodalsDealId: this.renderedAdData?.nodalsDealId ?? undefined,
        selectionLabel: this.getSelectionLabel(deliveryId, AdIdType.DELIVERY_ID),
        awayDuration: awayDurationMs,
        source: 'newtab',
        t: this.isLearningCampaignDeliveryId(deliveryId) ? 1 : 0,
      });
    });
  }

  public get vendorFormatDetection(): FormatDetection | null {
    return this.formatDetection;
  }

  /**
   * Identify whether the current render is a vendor takeover format. Only
   * placements declaring vendors they can host participate (see
   * getSupportedVendorFormats) — everyone else returns before allocating an
   * adapter cache, touching the DOM or scheduling a re-check. For those that
   * do, the factory returns adapters for every declared vendor whose engine
   * the page-level probe finds (an empty array on vendor-free pages), and the
   * first adapter to recognise the render wins. When the vendor's markers are
   * injected after the render, a bounded re-check completes the detection
   * later.
   */
  private detectVendorFormatForRender(bidAdMarkup?: string): void {
    const supported = this.getSupportedVendorFormats();
    if (supported.length === 0) {
      return;
    }
    const adapters = createVendorFormatAdapters(
      (this.vendorFormatAdapterCache ??= new Map()),
      supported
    );
    for (const adapter of adapters) {
      const detection = adapter.inspectRender(this.wrappingDomElement, bidAdMarkup);
      if (detection) {
        this.formatDetection = detection;
        logger.info(
          `Vendor format detected for ${this.adUnitCode}: ${detection.vendor}/${detection.formatId} (${detection.groupId})`
        );
        this.armFormatInteractionCapture(adapter);
        // A synchronous detection can be partial: the format id rides on the
        // wallpaper elements, which the vendor engine injects after fetching
        // its config. Keep re-checking so the detection completes.
        if (detection.formatId === null || detection.groupId === null) {
          adapter.watchForLateDetection(this.wrappingDomElement, (enriched) => {
            this.formatDetection = enriched;
            logger.info(
              `Vendor format detection enriched for ${this.adUnitCode}: ${enriched.formatId} (${enriched.groupId})`
            );
          });
        }
        return;
      }
    }
    // No adapter recognised the render synchronously: watch them all; the
    // first to detect wins and later callbacks are ignored.
    adapters.forEach((adapter) => {
      adapter.watchForLateDetection(this.wrappingDomElement, (lateDetection) => {
        if (this.formatDetection) {
          return;
        }
        this.formatDetection = lateDetection;
        logger.info(
          `Vendor format detected late for ${this.adUnitCode}: ${lateDetection.vendor}/${lateDetection.formatId} (${lateDetection.groupId})`
        );
        this.armFormatInteractionCapture(adapter);
      });
    });
  }

  private armFormatInteractionCapture(adapter: VendorFormatAdapter): void {
    // Vendor-format engagement signals (skin wallpaper clicks etc.) are
    // heuristic, exactly like the creative click/focus listeners: the vendor
    // frameworks surface ANY engagement with the format's surfaces, not just
    // clickthroughs. Route them through the navigation-evidence gate. On
    // Gumtree, ungated skin captures were the residual top-banner over-count
    // after the gate shipped (v1.83.0 rollout reconciliation vs GAM).
    adapter.armInteractionCapture((trigger) => {
      if (this.registerInteractionCandidate(trigger)) {
        debugPixel({ en: trigger });
      }
    });
  }

  protected handleRenderedAdData(adData: RenderedAdData): void {
    const isUntaggedMonitoredLine = this.getUntaggedMonitoredLineIds().includes(
      adData.lineItemId as string
    );
    const isUntaggedMonitoredCreative = this.getUntaggedMonitoredCreativeIds().includes(
      adData.creativeId as string
    );
    const isNodalsAdvertiser =
      adData.advertiserId && adData.advertiserId === this.getSiteConfig().nodalsAdvertiserId;
    const isNodalsTagged = isNodalsAdvertiser || adData.isNodalsTag;

    if (isUntaggedMonitoredLine || isUntaggedMonitoredCreative || isNodalsTagged) {
      if (isNodalsTagged) {
        logger.debug(
          `Expecting tagged ad (advertiser: ${adData.advertiserId}, nodalsTag: ${!!adData.isNodalsTag}) for ${this.adUnitCode}`
        );
        this.partialAdData = {
          advertiserId: adData.advertiserId,
          lineItemId: adData.lineItemId,
          creativeId: adData.creativeId,
          isProgrammaticBuy: false,
          size: adData.size,
        };
        return;
      } else {
        logger.info(
          `Untagged monitored ad (line: ${adData.lineItemId}, creative: ${adData.creativeId}) response for ${this.adUnitCode}.`
        );
        this.renderedAdData = adData;

        // Try to match using both line and creative IDs for most precise
        // match, falling back to line-only then creative-only. Track the ID
        // combination that produced the match so campaign attribution below
        // resolves with the same combination — an ad linked by line ID only
        // matches via the line-only fallback, and looking its campaign up
        // with both IDs would find nothing and wrongly demote the render to
        // a learning impression.
        let matchedLineId: string | undefined = adData.lineItemId as string;
        let matchedCreativeId: string | undefined = adData.creativeId as string;
        let deliveryId = this.getMatchingAdDeliveryId(matchedLineId, matchedCreativeId);

        // Fall back to line ID only if exact match not found
        if (!deliveryId && adData.lineItemId) {
          matchedCreativeId = undefined;
          deliveryId = this.getMatchingAdDeliveryId(matchedLineId, undefined);
        }

        // Fall back to creative ID only if still no match
        if (!deliveryId && adData.creativeId) {
          matchedLineId = undefined;
          matchedCreativeId = adData.creativeId as string;
          deliveryId = this.getMatchingAdDeliveryId(undefined, matchedCreativeId);
        }

        if (deliveryId) {
          const matchedCampaign = this.getAssociatedCampaign(matchedLineId, matchedCreativeId);
          const keyValues = matchedCampaign?.getAdKeyValues(deliveryId, AdIdType.DELIVERY_ID);

          if (
            !matchedCampaign ||
            (matchedCampaign.type === CampaignType.SCORING_DIRECT && !keyValues?.length)
          ) {
            this.processDirectLearningOpportunity();
            return;
          }

          this.dispatchDirectServedAdImpression(deliveryId, keyValues || undefined);
          // Untagged path only: the tagged path attaches its interaction
          // listeners in handleTaggedAdDelivery, once the impression is
          // recorded.
          this.addAdInteractionListener();
        }
      }
    } else if (!adData.isPrebidAd) {
      this.renderedAdData = adData;
      this.processDirectLearningOpportunity();
    } else {
      logger.info(`Programmatic learning opportunity for ${this.adUnitCode}.`);
      this.enablePrebidRenderListenerForOpportunity = true;
    }
  }

  private processDirectLearningOpportunity(): void {
    logger.info(`Direct learning opportunity for ${this.adUnitCode}.`);
    const deliveryId = this.getLearningCampaignDeliveryId(CampaignType.LEARNING_DIRECT);
    if (deliveryId) {
      this.dispatchDirectServedAdImpression(deliveryId);
      this.addAdInteractionListener();
    }
  }

  /**
   * Method to be implemented by subclasses to determine additional
   * sizes supported by the placement beyond those defined in the Prebid configuration.
   */
  protected abstract setSupportedSizes(): void;
  protected abstract determineVisibility(): void;

  /**
   * Method to be implemented by subclasses to set the native slot
   * object of the tagging system (e.g., GPT). a slot property
   * _must_ be set by the implementation in order for the placement
   * to be considered valid.
   */
  protected abstract setSlot(): void;
  protected abstract setWrappingDomElement(): void;

  /**
   * Method to be implemented by subclasses to add event listeners
   * relevant to the specific tagging system (e.g., GPT).
   * This is invoked after the native slot has been determined.
   */
  protected abstract addListeners(): void;
  protected abstract addAdInteractionListener(): void;

  private activateBrandSafetyMonitor(): void {
    if (this.brandSafetyActivated) {
      return;
    }
    this._campaigns.forEach((campaign) => {
      if (!campaign.hasEligibleAds()) {
        return;
      }
      campaign.on('BrandSafetyComplianceViolation', (event) => {
        const deduplicatedWords = [...new Set(event.data.detectedWords)];
        this.brandSafetyViolations.set(campaign.id, {
          detectedWords: deduplicatedWords,
        });
        this.evaluateBrandSafetyStatus();
      });

      campaign.on('BrandSafetyComplianceRestored', () => {
        this.brandSafetyViolations.delete(campaign.id);
        this.evaluateBrandSafetyStatus();
      });

      campaign.enableBrandSafetyMonitoring(this.siteConfig.contentQuerySelectors, () => {
        // Outermost gate: brand safety does no DOM scanning while this placement is
        // off-screen. Recompute visibility fresh on each check.
        this.determineVisibility();
        return this._visibility === Visibility.VISIBLE;
      });
    });
    this.brandSafetyActivated = true;
  }

  private evaluateBrandSafetyStatus(): void {
    let anyViolations = false;
    this.determineVisibility();
    this._campaigns.forEach((campaign) => {
      const campaignId = campaign.id;
      const violationData = this.brandSafetyViolations.get(campaignId);
      if (!violationData) {
        return;
      }

      if (
        !this.slotRenderedForOpportunity &&
        !this.recordedBrandSafetyViolationsForOpportunity.has(campaignId)
      ) {
        // We've not rendered an ad yet, but there's a brand safety violation.
        // No requirement to blur as we will not set our key-values.
        this.recordedBrandSafetyViolationsForOpportunity.add(campaignId);
        this.dispatch('BrandSafetyViolation', {
          bidStage: 'pre',
          campaignId: campaignId,
          detectedWords: violationData.detectedWords,
        });
      } else if (campaign.adDeliveryIds.includes(this.impressionDeliveryId as string)) {
        // We've rendered an ad for this campaign
        this.blur();
        anyViolations = true;

        if (
          this._visibility === Visibility.VISIBLE &&
          !this.recordedBrandSafetyViolationsForOpportunity.has(campaignId)
        ) {
          this.recordedBrandSafetyViolationsForOpportunity.add(campaignId);
          this.dispatch('BrandSafetyViolation', {
            bidStage: 'post',
            campaignId: campaignId,
            detectedWords: violationData.detectedWords,
          });
        }
      }
    });

    if (!anyViolations) {
      this.unblur();
    }
  }

  /**
   * The ad server ad unit path for this placement (e.g. a GPT slot's
   * getAdUnitPath()), or null where the tagging system has no such concept
   * or the slot has not been located yet. Used for the placement.adunit fact
   * so imported ad server inventory targeting can be evaluated client-side;
   * rules on the fact are skipped (fail open) while it is unavailable.
   */
  protected getAdUnitPath(): string | null {
    return null;
  }

  /**
   * Vendor takeover formats (skins, wallpapers, rich-media banners) this
   * placement can host. Empty by default: takeover buys are trafficked as ad
   * server line items into ordinary display slots, so only the GPT slice hosts
   * them today, and every other placement skips detection entirely. A tagging
   * slice widens support by returning the vendors its inventory can carry —
   * per vendor, so it takes on no adapter it does not need.
   */
  protected getSupportedVendorFormats(): readonly VendorFormat[] {
    return NO_VENDOR_FORMATS;
  }

  private setFacts(): void {
    this._facts['placement.visible'] = this.visibility;
    this._facts['placement.code'] = this.adUnitCode;
    const adUnitPath = this.getAdUnitPath();
    if (adUnitPath) {
      this._facts['placement.adunit'] = adUnitPath;
    }
    // The native token is appended to the FACT only, never to supportedSizes:
    // supportedSizes feeds the selection payload and viewability scoring,
    // which expect real dimensions.
    const sizes = this.supportedSizes.map((size: PlacementSize) => {
      if (!size) {
        return DIMENSIONS_UNKNOWN;
      }
      return `${size.width}x${size.height}`;
    });
    if (this.supportsNative) {
      sizes.push(NATIVE_SIZE_TOKEN);
    }
    this._facts['placement.sizes'] = sizes.length > 0 ? sizes : [DIMENSIONS_UNKNOWN];
  }

  /**
   * Initialises the supported sizes based on Prebid configuration.
   * If no sizes are found, the supported sizes will remain empty.
   *
   * TODO: Add support for https://docs.prebid.org/dev-docs/modules/sizeMappingV2.html
   */
  private initialiseSupportedSizes(): void {
    // Native capability is independent of banner/video sizes: a multiformat
    // ad unit carries both real sizes and the native token in its facts.
    if (this.adUnit.mediaTypes?.native) {
      this.supportsNative = true;
    }
    if (this.adUnit.mediaTypes?.banner) {
      const banner = this.adUnit.mediaTypes.banner;
      if (Array.isArray(banner.sizes)) {
        banner.sizes.forEach((size) => {
          this.addSupportedSize({ width: size[0], height: size[1] });
        });
      }
    } else if (this.adUnit.mediaTypes?.video) {
      const video = this.adUnit.mediaTypes.video;
      if (Array.isArray(video.playerSize)) {
        const videoPlayerSizes = video.playerSize || [];
        videoPlayerSizes.forEach((size: PrebidAdUnitSize) => {
          this.addSupportedSize({ width: size[0], height: size[1] });
        });
      }
    }
  }

  private blur(): void {
    if (this.wrappingDomElement && !this.isBlurred) {
      this.originalElementFilter = getComputedStyle(this.wrappingDomElement).filter;
      this.wrappingDomElement.style.filter = this.wrappingDomElement.style.webkitFilter =
        this.originalElementFilter === 'none'
          ? 'blur(10px)'
          : `${this.originalElementFilter} blur(10px)`;
      this.isBlurred = true;
    }
  }

  private unblur(): void {
    if (this.wrappingDomElement && this.isBlurred) {
      this.wrappingDomElement.style.filter = this.wrappingDomElement.style.webkitFilter =
        this.originalElementFilter;
      this.isBlurred = false;
    }
  }

  private isElementHidden(
    element: Element | null | undefined,
    checkOpacity: boolean = false
  ): boolean {
    if (!element) {
      return true;
    }

    let currentElement: Element | null = element;

    while (currentElement) {
      const styles: CSSStyleDeclaration = window.getComputedStyle(currentElement);
      if (styles.display === 'none' || styles.visibility === 'hidden') {
        return true;
      }
      if (checkOpacity && parseFloat(styles.opacity) === 0) {
        return true;
      }

      const root: Document | ShadowRoot = currentElement.getRootNode() as Document | ShadowRoot;

      if (root instanceof ShadowRoot) {
        currentElement = root.host;
      } else if (currentElement.parentElement) {
        currentElement = currentElement.parentElement;
      } else {
        break;
      }
    }

    return false;
  }

  protected addIframedAdInteractionListener(): void {
    if (this.slot === null || this.interactionListenerAdded) {
      return;
    }
    logger.info(`Adding ad interaction listener for ${this.adUnitCode}`);
    const errorPrefix = `Unable to add ad interaction listener for ${this.adUnitCode}:`;
    const slotParent = this.wrappingDomElement;
    if (slotParent) {
      this.placementFrame = slotParent.querySelector('iframe');
      if (this.placementFrame) {
        try {
          const nativeWinOpen = this.placementFrame.contentWindow?.open;
          if (nativeWinOpen) {
            this.nativeWindowOpen = nativeWinOpen;
            // @ts-ignore
            this.placementFrame.contentWindow.open = (...args) => {
              // The creative invoking window.open IS the navigation — dispatch
              // immediately, no evidence wait. Skip the hidden check for the
              // same reason as confirmed candidates: the container may tear
              // the slot down as the navigation happens.
              this.clearPendingInteractionCandidate();
              if (
                this.dispatchAdInteraction(false, {
                  immediate: true,
                  skipHiddenCheck: true,
                })
              ) {
                debugPixel({
                  en: 'AdInteractionTriggerPlacementFrameWindowOpen',
                });
              }
              return nativeWinOpen.apply(this.placementFrame?.contentWindow, args);
            };
            this.iframeFocusListener = () => {
              if (this.registerInteractionCandidate('frameContentWindowFocus')) {
                debugPixel({
                  en: 'AdInteractionTriggerFrameContentWindowFocus',
                });
              }
            };
            // @ts-ignore
            this.placementFrame.contentWindow.addEventListener('focus', this.iframeFocusListener);

            this.iframeClickListener = () => {
              if (this.registerInteractionCandidate('frameContentWindowClick')) {
                debugPixel({
                  en: 'AdInteractionTriggerFrameContentWindowClick',
                });
              }
            };
            this.placementFrame.contentWindow?.document.addEventListener(
              'click',
              this.iframeClickListener
            );
            logger.debug(`Added focus and click listeners frame for: ${this.adUnitCode}`);
          }
        } catch (e) {
          logger.debug(
            `Unable to overwrite window opener for placement frame for ${this.adUnitCode}, error: ${e}`
          );
        } finally {
          this.addWindowBlurListeners();
        }
      } else {
        logger.warn(`${errorPrefix} ad iframe not found`);
      }
    } else {
      logger.warn(`${errorPrefix} iframe parent element not found`);
    }
    this.interactionListenerAdded = true;
  }

  private addWindowBlurListeners(): void {
    if (this.placementFrame) {
      this.iframeMouseEnterListener = () => {
        this.mouseEnteredIframe = true;
      };
      this.iframeMouseLeaveListener = () => {
        this.mouseEnteredIframe = false;
      };
      this.placementFrame.addEventListener('mouseenter', this.iframeMouseEnterListener);
      this.placementFrame.addEventListener('mouseleave', this.iframeMouseLeaveListener);
    }

    this.removeBlurEventListener();
    this.winBlurListener = () => {
      this.documentBlurHandler();
    };
    window.addEventListener('blur', this.winBlurListener);

    // Navigation-evidence listeners: confirm pending interaction candidates
    // when the page actually leaves view (a clickthrough opening its new tab
    // fronts the new tab -> visibilitychange 'hidden'; same-tab navigations
    // and bfcache exits fire pagehide, which is also the more reliable of the
    // two on iOS Safari).
    this.ensureNavigationEvidenceListeners();
  }

  /**
   * Install the navigation-evidence listeners exactly once per render.
   * Called from addWindowBlurListeners (iframed display) AND from
   * registerInteractionCandidate, because some candidate sources — vendor
   * format captures (skins) in particular — arm their own engagement
   * listeners without ever going through addIframedAdInteractionListener.
   * A candidate without these listeners could never confirm.
   */
  private ensureNavigationEvidenceListeners(): void {
    if (this.pageVisibilityListener) {
      return;
    }
    this.pageVisibilityListener = () => {
      if (document.visibilityState === 'hidden') {
        this.onPossibleNavigation('hidden');
      }
    };
    document.addEventListener('visibilitychange', this.pageVisibilityListener);
    this.pageHideListener = () => {
      this.onPossibleNavigation('pagehide');
    };
    window.addEventListener('pagehide', this.pageHideListener);
  }

  private removeBlurEventListener(): void {
    if (this.winBlurListener) {
      window.removeEventListener('blur', this.winBlurListener);
    }
  }

  /**
   * Remove the per-render ad-interaction listeners. Invoked from
   * resetStateForNewRender() when the slot re-renders, so listeners armed for a
   * prior render cannot fire against the ad that replaced it. Null-safe and
   * idempotent.
   */
  protected removeAdInteractionListeners(): void {
    const contentWindow = this.placementFrame?.contentWindow ?? null;
    if (contentWindow) {
      // Restore the original window.open we overrode, if still patched.
      if (this.nativeWindowOpen) {
        // @ts-ignore
        contentWindow.open = this.nativeWindowOpen;
      }
      if (this.iframeFocusListener) {
        // @ts-ignore
        contentWindow.removeEventListener('focus', this.iframeFocusListener);
      }
      if (this.iframeClickListener) {
        try {
          contentWindow.document.removeEventListener('click', this.iframeClickListener);
        } catch (e) {
          // cross-origin frame teardown can throw on document access; ignore.
        }
      }
    }
    if (this.placementFrame) {
      if (this.iframeMouseEnterListener) {
        this.placementFrame.removeEventListener('mouseenter', this.iframeMouseEnterListener);
      }
      if (this.iframeMouseLeaveListener) {
        this.placementFrame.removeEventListener('mouseleave', this.iframeMouseLeaveListener);
      }
    }
    this.removeBlurEventListener();

    if (this.pageVisibilityListener) {
      document.removeEventListener('visibilitychange', this.pageVisibilityListener);
    }
    if (this.pageHideListener) {
      window.removeEventListener('pagehide', this.pageHideListener);
    }
    // Drop any open candidate with the listeners: it belonged to the render
    // being torn down and must not confirm against the one replacing it.
    this.clearPendingInteractionCandidate();

    this.nativeWindowOpen = undefined;
    this.iframeFocusListener = undefined;
    this.iframeClickListener = undefined;
    this.iframeMouseEnterListener = undefined;
    this.iframeMouseLeaveListener = undefined;
    this.winBlurListener = undefined;
    this.pageVisibilityListener = undefined;
    this.pageHideListener = undefined;
  }

  private documentBlurHandler(): void {
    if (!this.placementFrame) {
      return;
    }
    logger.info(`Processing blur event for ${this.adUnitCode}`);
    const logMsgPrefix = `Blur handler opening interaction candidate for ${this.adUnitCode} via `;

    // NOTE: the blur listener is no longer removed on first trigger. A blur
    // candidate that expires unconfirmed (focus entered the creative without a
    // navigation) must leave the listener in place so a later genuine click
    // can open a fresh candidate. Termination comes from the per-render
    // interaction latch and render-scoped teardown, not from one-shot removal.

    // Direct iframe focus (works for cross-origin)
    if (document.activeElement === this.placementFrame) {
      logger.info(`${logMsgPrefix} active ele match.`);
      if (this.registerInteractionCandidate('blurActiveElement')) {
        debugPixel({
          en: 'AdInteractionTriggerBlurActiveElement',
        });
      }
      return;
    }

    // Same-origin nested iframe detection
    try {
      const nestedDocument = this.placementFrame.contentWindow?.document as Document;
      const nestedIframes = [...nestedDocument.querySelectorAll('iframe')];

      if (this.mouseEnteredIframe && nestedIframes.length > 0) {
        logger.info(`${logMsgPrefix} nested frame becoming active.`);
        if (this.registerInteractionCandidate('nestedIframeActive')) {
          debugPixel({
            en: 'AdInteractionTriggerNestedIframeActive',
          });
        }
        return;
      }
    } catch (e) {}
  }
}

export { Placement };
